import React, { useState } from "react";
import {
  HeartHandshake,
  Home,
  Brain,
  ChevronDown,
} from "lucide-react";

const services = [
  {
    key: "personal",
    icon: HeartHandshake,
    title: "Personal Care Assistance",
    summary:
      "Dignified, respectful help with hygiene, dressing, mobility, and daily living activities.",
    details: [
      "Bathing, grooming & hygiene support",
      "Mobility transfers & safe ambulation",
      "Toileting & continence care",
      "Medication reminders",
    ],
  },
  {
    key: "companionship",
    icon: Home,
    title: "Companionship & Home Support",
    summary:
      "Genuine human connection plus practical help that keeps your home running smoothly.",
    details: [
      "Meal planning & preparation",
      "Light housekeeping & laundry",
      "Errands, shopping & appointments",
      "Friendly conversation & activities",
    ],
  },
  {
    key: "specialized",
    icon: Brain,
    title: "Specialized Care",
    summary:
      "Tailored programs for complex needs — from dementia support to family respite.",
    details: [
      "Alzheimer's & dementia care",
      "Respite care for family caregivers",
      "Post-hospital recovery",
      "Postpartum & newborn support",
    ],
  },
];

export const Services = () => {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="section-padding bg-[#F4F7F6]"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow" data-testid="services-eyebrow">Our Core Services</div>
          <h2
            className="font-display text-3xl md:text-5xl font-semibold mt-3 aegis-navy-text"
            data-testid="services-heading"
          >
            Tailored care for every chapter of life
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            From a few hours a week to round-the-clock support — every care plan
            is built around your loved one, never a template.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = s.icon;
            const expanded = open === s.key;
            return (
              <div
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="service-card flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-[#E3EEEC] text-[#2A5C56]">
                  <Icon size={24} />
                </div>
                <h3 className="font-display text-xl font-semibold aegis-navy-text">
                  {s.title}
                </h3>
                <p className="mt-3 text-gray-600 leading-relaxed text-[15px]">
                  {s.summary}
                </p>

                <button
                  type="button"
                  data-testid={`service-toggle-${s.key}`}
                  onClick={() => setOpen(expanded ? null : s.key)}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#3D7A73] hover:text-[#2A5C56]"
                  aria-expanded={expanded}
                >
                  {expanded ? "Hide details" : "What's included"}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                  />
                </button>

                {expanded && (
                  <ul
                    data-testid={`service-details-${s.key}`}
                    className="mt-4 space-y-2 text-sm text-gray-700 border-t pt-4"
                  >
                    {s.details.map((d) => (
                      <li key={d} className="flex gap-2">
                        <span className="text-[#3D7A73] mt-1">•</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
